import {useMemo} from 'react'

import type {DateValue} from '../../../../api/columns/contracts/date'
import type {Iteration} from '../../../../api/columns/contracts/iteration'
import {MemexColumnDataType, SystemColumnId} from '../../../../api/columns/contracts/memex-column'
import type {MultiSelectValue} from '../../../../api/columns/contracts/multi-select'
import type {NumericValue} from '../../../../api/columns/contracts/number'
import type {SingleSelectValue} from '../../../../api/columns/contracts/single-select'
import type {EnrichedText} from '../../../../api/columns/contracts/text'
import type {TitleValueWithContentType} from '../../../../api/columns/contracts/title'
import type {
  ExtendedRepository,
  IssueType,
  Label,
  LinkedPullRequest,
  Milestone,
  ParentIssue,
  SubIssuesProgress,
} from '../../../../api/common-contracts'
import {ItemType} from '../../../../api/memex-items/item-type'
import {getVisibleAgentTasks} from '../../../../components/table/agent-task-utils'
import {itemFromAssignee, itemFromReview, type UserGroupItem} from '../../../../components/table/cells/user-group'
import {AssigneesEditor} from '../../../../components/table/editors/assignees-editor'
import {DateEditor} from '../../../../components/table/editors/date-editor'
import {IssueTypeEditor} from '../../../../components/table/editors/issue-type-editor'
import {IterationEditor} from '../../../../components/table/editors/iteration-editor'
import {LabelsEditor} from '../../../../components/table/editors/labels-editor'
import {MilestoneEditor} from '../../../../components/table/editors/milestone-editor'
import {MultiSelectEditor} from '../../../../components/table/editors/multi-select-editor'
import {NumberEditor} from '../../../../components/table/editors/number-editor'
import {ParentIssueEditor} from '../../../../components/table/editors/parent-issue-editor'
import {RepositoryEditor} from '../../../../components/table/editors/repository-editor'
import {SingleSelectEditor} from '../../../../components/table/editors/single-select-editor'
import {TextEditor} from '../../../../components/table/editors/text-editor'
import {TitleEditor} from '../../../../components/table/editors/title-editor'
import {AssigneesRenderer, LoadingAssigneesCell} from '../../../../components/table/renderers/assignees-renderer'
import {DateRenderer, LoadingDateCell} from '../../../../components/table/renderers/date-renderer'
import {IssueTypeRenderer, LoadingIssueTypeCell} from '../../../../components/table/renderers/issue-type-renderer'
import {IterationRenderer, LoadingIterationCell} from '../../../../components/table/renderers/iteration-renderer'
import {LabelsRenderer, LoadingLabelsCell} from '../../../../components/table/renderers/labels-renderer'
import {
  LinkedPullRequestsRenderer,
  LoadingLinkedPullRequestsCell,
} from '../../../../components/table/renderers/linked-pull-requests-renderer'
import {LoadingMilestoneCell, MilestoneRenderer} from '../../../../components/table/renderers/milestone-renderer'
import {MissingSubIssuesRenderer} from '../../../../components/table/renderers/missing-sub-issues-renderer'
import {LoadingMultiSelectCell, MultiSelectRenderer} from '../../../../components/table/renderers/multi-select-renderer'
import {LoadingNumberCell, NumberRenderer} from '../../../../components/table/renderers/number-renderer'
import {LoadingParentIssueCell, ParentIssueRenderer} from '../../../../components/table/renderers/parent-issue-renderer'
import {LoadingRepositoryCell, RepositoryRenderer} from '../../../../components/table/renderers/repository-renderer'
import {LoadingReviewersCell, ReviewersRenderer} from '../../../../components/table/renderers/reviewers-renderer'
import {
  LoadingSingleSelectCell,
  SingleSelectRenderer,
} from '../../../../components/table/renderers/single-select-renderer'
import {
  LoadingSubIssuesProgressCell,
  SubIssuesProgressRenderer,
} from '../../../../components/table/renderers/sub-issues-progress-renderer'
import {LoadingTextCell, TextRenderer} from '../../../../components/table/renderers/text-renderer'
import {LoadingTitleCell, TitleRenderer} from '../../../../components/table/renderers/title-renderer'
import type {CustomColumnMeta} from '../../../../components/table/state-providers/table-columns/types'
import type {TableDataType} from '../../../../components/table/table-data-type'
import {getAllIterations} from '../../../../helpers/iterations'
import {
  asCustomDateValue,
  asCustomNumberValue,
  asCustomTextValue,
  asMultiSelectValue,
  asSingleSelectValue,
  formatFullTimestamp,
} from '../../../../helpers/parsing'
import type {ColumnModel} from '../../../../models/column-model'
import {isIterationColumnModel} from '../../../../models/column-model/guards'
import {type ColumnValue, EmptyValue, LoadingValue, withValue} from '../../../../models/column-value'
import {itemHasColumnLoaded} from '../../../../state-providers/memex-items/memex-items-data'
import {Resources} from '../../../../strings'
import type {ColumnDef} from '../../helpers/types'

type PartialCustomColumnMeta = Pick<CustomColumnMeta, 'Cell' | 'CellEditor' | 'Placeholder'> & {
  typeToEditEnabled?: boolean
  nonNavigable?: boolean
  /**
   * Unless 'nullWhenRedacted' is explicitly configured as false,
   * the Cell renderer will return null for redacted items.
   */
  nullWhenRedacted?: boolean
}
type PartialCustomColumnMetaLookup = {[P in MemexColumnDataType]: PartialCustomColumnMeta}

/** Shared cell rendering definition for timestamp fields */
function timestampColumnMeta(
  columnId: typeof SystemColumnId.Created | typeof SystemColumnId.Updated | typeof SystemColumnId.Closed,
): PartialCustomColumnMeta {
  return {
    typeToEditEnabled: false,
    Placeholder: <LoadingDateCell />,
    Cell({row, isDisabled}) {
      const columnData = row.original.columns[columnId]
      const isLoaded = itemHasColumnLoaded(row.original, columnId)

      const currentValue = useMemo<ColumnValue<DateValue>>(() => {
        if (!isLoaded) return LoadingValue
        if (columnData) return withValue({value: new Date(columnData.value)})
        return EmptyValue
      }, [columnData, isLoaded])

      const tooltipText = useMemo(() => {
        if (!columnData) return undefined
        return formatFullTimestamp(new Date(columnData.value))
      }, [columnData])

      return (
        <DateRenderer
          currentValue={currentValue}
          model={row.original}
          isDisabled={isDisabled}
          tooltipText={tooltipText}
          localTimezone
        />
      )
    },
  }
}

const columnMetaByFieldDataType: PartialCustomColumnMetaLookup = {
  title: {
    nullWhenRedacted: false,
    typeToEditEnabled: false,
    Placeholder: <LoadingTitleCell />,
    Cell({row, isDisabled, expandRef, isExpanded}) {
      const columnId = SystemColumnId.Title

      const columnData = row.original.columns[columnId]

      const isLoaded = itemHasColumnLoaded(row.original, columnId)

      const {missingSubIssues = [], subIssueDepth = 0} = row.subIssueData ?? {}

      const currentValue = useMemo<ColumnValue<TitleValueWithContentType>>(() => {
        if (!isLoaded) {
          return LoadingValue
        }

        if (columnData) {
          return withValue(columnData)
        }

        return EmptyValue
      }, [columnData, isLoaded])

      if (missingSubIssues.length > 0) {
        return <MissingSubIssuesRenderer missingSubIssues={missingSubIssues} subIssueDepth={subIssueDepth} />
      }

      const subIssuesProgress = row.original.getSubIssuesProgress()
      const hasSubIssueHierarchy = !!subIssuesProgress && subIssuesProgress.total > 0
      const hasVisibleAgentTasks = getVisibleAgentTasks(row.original).length > 0
      const isActuallyExpandable = row.getCanExpand() && (hasSubIssueHierarchy || hasVisibleAgentTasks)

      return (
        <TitleRenderer
          currentValue={currentValue}
          model={row.original}
          isDisabled={isDisabled}
          subIssueDepth={row.subIssueData?.subIssueDepth}
          expandRef={expandRef}
          expandable={isActuallyExpandable}
          expanded={isExpanded}
          rowId={row.id}
        />
      )
    },
    CellEditor({row, replaceContents}) {
      const item = row.original

      const columnId = SystemColumnId.Title

      const columnData = row.original.columns[columnId]

      const isLoaded = itemHasColumnLoaded(row.original, columnId)

      const currentValue = useMemo<ColumnValue<TitleValueWithContentType>>(() => {
        if (!isLoaded) {
          return LoadingValue
        }

        if (columnData) {
          return withValue(columnData)
        }

        return EmptyValue
      }, [columnData, isLoaded])

      return <TitleEditor model={item} replaceContents={replaceContents} currentValue={currentValue} />
    },
  },
  text: {
    Placeholder: <LoadingTextCell />,
    Cell({row, column, isDisabled}) {
      const columnId = column.columnModel?.id || -1

      const columnData = row.original.columns[columnId]

      const isLoaded = itemHasColumnLoaded(row.original, columnId)

      const currentValue = useMemo<ColumnValue<EnrichedText>>(() => {
        if (!isLoaded) {
          return LoadingValue
        }

        const value = asCustomTextValue(columnData)

        if (value) {
          return withValue(value)
        }

        return EmptyValue
      }, [columnData, isLoaded])

      return <TextRenderer currentValue={currentValue} model={row.original} isDisabled={isDisabled} />
    },
    CellEditor({row, column, replaceContents}) {
      const item = row.original

      if (!column.columnModel || !column.columnModel.userDefined) {
        throw Error(`${Resources.textEditor} ${Resources.requiresCustomColumn}`)
      }

      const columnId = column.columnModel.id

      const columnData = row.original.columns[columnId]

      const isLoaded = itemHasColumnLoaded(row.original, columnId)

      const currentValue = useMemo<ColumnValue<EnrichedText>>(() => {
        if (!isLoaded) {
          return LoadingValue
        }

        const value = asCustomTextValue(columnData)

        if (value) {
          return withValue(value)
        }

        return EmptyValue
      }, [columnData, isLoaded])

      return (
        <TextEditor
          currentValue={currentValue}
          model={item}
          replaceContents={replaceContents}
          columnId={columnId}
          rowId={row.id}
          isIssueField={column.columnModel.isIssueField}
        />
      )
    },
  },
  number: {
    Placeholder: <LoadingNumberCell />,
    Cell({row, column, isDisabled}) {
      const columnId = column.columnModel?.id || -1

      const columnData = row.original.columns[columnId]

      const isLoaded = itemHasColumnLoaded(row.original, columnId)

      const currentValue = useMemo<ColumnValue<NumericValue>>(() => {
        if (!isLoaded) {
          return LoadingValue
        }

        const value = asCustomNumberValue(columnData)

        if (value) {
          return withValue(value)
        }

        return EmptyValue
      }, [columnData, isLoaded])

      return <NumberRenderer currentValue={currentValue} model={row.original} isDisabled={isDisabled} />
    },
    CellEditor({row, column, replaceContents}) {
      const item = row.original

      if (!column.columnModel || !column.columnModel.userDefined) {
        throw Error(`${Resources.numberEditor} ${Resources.requiresCustomColumn}`)
      }

      const columnId = column.columnModel.id

      const columnData = row.original.columns[columnId]

      const isLoaded = itemHasColumnLoaded(row.original, columnId)

      const currentValue = useMemo<ColumnValue<NumericValue>>(() => {
        if (!isLoaded) {
          return LoadingValue
        }

        const value = asCustomNumberValue(columnData)

        if (value) {
          return withValue(value)
        }

        return EmptyValue
      }, [columnData, isLoaded])

      return (
        <NumberEditor
          currentValue={currentValue}
          model={item}
          replaceContents={replaceContents}
          rowId={row.id}
          columnId={columnId}
          isIssueField={column.columnModel.isIssueField}
        />
      )
    },
  },
  singleSelect: {
    Placeholder: <LoadingSingleSelectCell />,
    Cell({row, column, dropdownRef, isDisabled}) {
      const columnId = column.columnModel?.id || -1
      const columnData = row.original.columns[columnId]
      const isLoaded = itemHasColumnLoaded(row.original, columnId)
      const currentValue = useMemo<ColumnValue<SingleSelectValue>>(() => {
        if (!isLoaded) return LoadingValue

        const value = asSingleSelectValue(columnData)
        if (value) return withValue(value)

        return EmptyValue
      }, [columnData, isLoaded])

      if (column.columnModel?.dataType !== MemexColumnDataType.SingleSelect) {
        throw new Error(Resources.singleSelectCustomModelError)
      }

      return (
        <SingleSelectRenderer
          currentValue={currentValue}
          model={row.original}
          options={column.columnModel.settings.options}
          dropdownRef={dropdownRef}
          isDisabled={isDisabled}
        />
      )
    },
    CellEditor({row, column}) {
      const item = row.original
      const columnId = column.columnModel?.id || -1
      const columnData = row.original.columns[columnId]
      const isLoaded = itemHasColumnLoaded(row.original, columnId)
      const currentValue = useMemo<ColumnValue<SingleSelectValue>>(() => {
        if (!isLoaded) return LoadingValue

        const value = asSingleSelectValue(columnData)
        if (value) return withValue(value)

        return EmptyValue
      }, [columnData, isLoaded])

      if (item.contentType === ItemType.RedactedItem) return null

      if (column.columnModel?.dataType !== MemexColumnDataType.SingleSelect) {
        throw Error(Resources.singleSelectCustomModelError)
      }

      return (
        <SingleSelectEditor
          currentValue={currentValue}
          model={item}
          rowIndex={row.index}
          columnModel={column.columnModel}
          columnId={columnId}
        />
      )
    },
  },
  multiSelect: {
    Placeholder: <LoadingMultiSelectCell />,
    Cell({row, column, dropdownRef, isDisabled}) {
      const columnId = column.columnModel?.id || -1
      const columnData = row.original.columns[columnId]
      const isLoaded = itemHasColumnLoaded(row.original, columnId)
      const currentValue = useMemo<ColumnValue<MultiSelectValue>>(() => {
        if (!isLoaded) return LoadingValue

        const value = asMultiSelectValue(columnData)
        if (value) return withValue(value)

        return EmptyValue
      }, [columnData, isLoaded])

      if (column.columnModel?.dataType !== MemexColumnDataType.MultiSelect) {
        throw new Error(Resources.multiSelectCustomModelError)
      }

      return (
        <MultiSelectRenderer
          currentValue={currentValue}
          model={row.original}
          options={column.columnModel.settings.options}
          dropdownRef={dropdownRef}
          isDisabled={isDisabled}
        />
      )
    },
    CellEditor({row, column}) {
      const item = row.original
      const columnId = column.columnModel?.id || -1
      const columnData = row.original.columns[columnId]
      const isLoaded = itemHasColumnLoaded(row.original, columnId)
      const currentValue = useMemo<ColumnValue<MultiSelectValue>>(() => {
        if (!isLoaded) return LoadingValue

        const value = asMultiSelectValue(columnData)
        if (value) return withValue(value)

        return EmptyValue
      }, [columnData, isLoaded])

      if (item.contentType === ItemType.RedactedItem) return null

      if (column.columnModel?.dataType !== MemexColumnDataType.MultiSelect) {
        throw new Error(Resources.multiSelectCustomModelError)
      }

      return (
        <MultiSelectEditor
          currentValue={currentValue}
          model={item}
          rowIndex={row.index}
          columnModel={column.columnModel}
          columnId={columnId}
        />
      )
    },
  },
  date: {
    Placeholder: <LoadingDateCell />,
    Cell({row, column, isDisabled}) {
      const columnId = column.columnModel?.id || -1

      const columnData = row.original.columns[columnId]

      const isLoaded = itemHasColumnLoaded(row.original, columnId)

      const currentValue = useMemo<ColumnValue<DateValue>>(() => {
        if (!isLoaded) {
          return LoadingValue
        }

        const value = asCustomDateValue(columnData)

        if (value) {
          return withValue(value)
        }

        return EmptyValue
      }, [columnData, isLoaded])

      return <DateRenderer currentValue={currentValue} model={row.original} isDisabled={isDisabled} />
    },
    CellEditor({row, column, replaceContents}) {
      const item = row.original

      if (!column.columnModel || !column.columnModel.userDefined) {
        throw Error(`${Resources.dateEditor} ${Resources.requiresCustomColumn}`)
      }

      const columnId = column.columnModel.id

      const columnData = row.original.columns[columnId]

      const isLoaded = itemHasColumnLoaded(row.original, columnId)

      const currentValue = useMemo<ColumnValue<DateValue>>(() => {
        if (!isLoaded) {
          return LoadingValue
        }

        const value = asCustomDateValue(columnData)

        if (value) {
          return withValue(value)
        }

        return EmptyValue
      }, [columnData, isLoaded])

      return (
        <DateEditor
          currentValue={currentValue}
          model={item}
          replaceContents={replaceContents}
          rowIndex={row.index}
          columnId={column.columnModel.id}
          isIssueField={column.columnModel.isIssueField}
        />
      )
    },
  },
  assignees: {
    Placeholder: <LoadingAssigneesCell />,
    Cell({row, dropdownRef, isDisabled}) {
      const columnId = SystemColumnId.Assignees

      const isLoaded = itemHasColumnLoaded(row.original, columnId)

      const columnData = row.original.columns[columnId]

      const currentValue = useMemo<ColumnValue<Array<UserGroupItem>>>(() => {
        if (!isLoaded) {
          return LoadingValue
        }

        if (columnData) {
          return withValue(columnData.map(itemFromAssignee))
        }

        return EmptyValue
      }, [columnData, isLoaded])

      return (
        <AssigneesRenderer
          currentValue={currentValue}
          model={row.original}
          dropdownRef={dropdownRef}
          isDisabled={isDisabled}
        />
      )
    },
    CellEditor({row}) {
      const item = row.original

      const columnId = SystemColumnId.Assignees

      const isLoaded = itemHasColumnLoaded(row.original, columnId)

      const columnData = row.original.columns[columnId]

      const currentValue = useMemo<ColumnValue<Array<UserGroupItem>>>(() => {
        if (!isLoaded) {
          return LoadingValue
        }

        if (columnData) {
          return withValue(columnData.map(itemFromAssignee))
        }

        return EmptyValue
      }, [columnData, isLoaded])

      if (item.contentType === ItemType.RedactedItem) {
        return null
      }

      return <AssigneesEditor currentValue={currentValue} model={item} rowIndex={row.index} columnId={columnId} />
    },
  },
  iteration: {
    Placeholder: <LoadingIterationCell />,
    Cell({row, column, dropdownRef, isDisabled}) {
      const columnId = column.columnModel?.id || -1

      const columnData = row.original.columns[columnId]

      const isLoaded = itemHasColumnLoaded(row.original, columnId)

      const currentValue = useMemo<ColumnValue<Iteration>>(() => {
        if (!isLoaded) {
          return LoadingValue
        }

        const value = asSingleSelectValue(columnData)

        if (!value) {
          return EmptyValue
        }

        const iterations = isIterationColumnModel(column.columnModel) ? getAllIterations(column.columnModel) : []
        const matchingOption = iterations.find(o => o.id === value.id)

        if (matchingOption) {
          return withValue(matchingOption)
        }

        return EmptyValue
      }, [columnData, isLoaded, column.columnModel])

      return (
        <IterationRenderer
          currentValue={currentValue}
          model={row.original}
          dropdownRef={dropdownRef}
          isDisabled={isDisabled}
        />
      )
    },
    CellEditor({row, column}) {
      const item = row.original

      const columnId = column.columnModel?.id || -1

      const columnData = row.original.columns[columnId]

      const isLoaded = itemHasColumnLoaded(row.original, columnId)

      const currentValue = useMemo<ColumnValue<Iteration>>(() => {
        if (!isLoaded) {
          return LoadingValue
        }

        const value = asSingleSelectValue(columnData)

        if (!value) {
          return EmptyValue
        }

        const iterations = isIterationColumnModel(column.columnModel) ? getAllIterations(column.columnModel) : []
        const matchingOption = iterations.find(o => o.id === value.id)

        if (matchingOption) {
          return withValue(matchingOption)
        }

        return EmptyValue
      }, [columnData, isLoaded, column.columnModel])

      if (item.contentType === ItemType.RedactedItem) {
        return null
      }

      if (column.columnModel?.dataType !== MemexColumnDataType.Iteration) {
        throw Error(Resources.iterationCustomModelError)
      }

      return (
        <IterationEditor
          currentValue={currentValue}
          model={item}
          rowIndex={row.index}
          columnModel={column.columnModel}
          columnId={columnId}
        />
      )
    },
  },
  milestone: {
    Placeholder: <LoadingMilestoneCell />,
    Cell({row, dropdownRef, isDisabled}) {
      const columnId = SystemColumnId.Milestone

      const columnData = row.original.columns[columnId]

      const isLoaded = itemHasColumnLoaded(row.original, columnId)

      const currentValue = useMemo<ColumnValue<Milestone>>(() => {
        if (!isLoaded) {
          return LoadingValue
        }

        if (columnData) {
          return withValue(columnData)
        }

        return EmptyValue
      }, [columnData, isLoaded])

      return (
        <MilestoneRenderer
          currentValue={currentValue}
          model={row.original}
          dropdownRef={dropdownRef}
          isDisabled={isDisabled}
        />
      )
    },
    CellEditor({row}) {
      const item = row.original

      const columnId = SystemColumnId.Milestone

      const columnData = row.original.columns[columnId]

      const isLoaded = itemHasColumnLoaded(row.original, columnId)

      const currentValue = useMemo<ColumnValue<Milestone>>(() => {
        if (!isLoaded) {
          return LoadingValue
        }

        if (columnData) {
          return withValue(columnData)
        }

        return EmptyValue
      }, [columnData, isLoaded])

      if (item.contentType === ItemType.RedactedItem) {
        return null
      }

      return <MilestoneEditor currentValue={currentValue} model={item} rowIndex={row.index} columnId={columnId} />
    },
  },
  labels: {
    Placeholder: <LoadingLabelsCell />,
    Cell({row, dropdownRef, isDisabled}) {
      const columnId = SystemColumnId.Labels

      const isLoaded = itemHasColumnLoaded(row.original, columnId)

      const columnData = row.original.columns[columnId]

      const currentValue = useMemo<ColumnValue<Array<Label>>>(() => {
        if (!isLoaded) {
          return LoadingValue
        }

        if (columnData) {
          return withValue(columnData)
        }

        return EmptyValue
      }, [columnData, isLoaded])

      return (
        <LabelsRenderer
          currentValue={currentValue}
          model={row.original}
          dropdownRef={dropdownRef}
          isDisabled={isDisabled}
        />
      )
    },
    CellEditor({row}) {
      const item = row.original

      const columnId = SystemColumnId.Labels

      const isLoaded = itemHasColumnLoaded(row.original, columnId)

      const columnData = row.original.columns[columnId]

      const currentValue = useMemo<ColumnValue<Array<Label>>>(() => {
        if (!isLoaded) {
          return LoadingValue
        }

        if (columnData) {
          return withValue(columnData)
        }

        return EmptyValue
      }, [columnData, isLoaded])

      if (item.contentType === ItemType.RedactedItem) {
        return null
      }

      return <LabelsEditor currentValue={currentValue} model={item} rowIndex={row.index} columnId={columnId} />
    },
  },
  repository: {
    Placeholder: <LoadingRepositoryCell />,
    Cell({row, dropdownRef, isDisabled}) {
      const columnId = SystemColumnId.Repository

      const isLoaded = itemHasColumnLoaded(row.original, columnId)

      const columnData = row.original.columns[columnId]

      const currentValue = useMemo<ColumnValue<ExtendedRepository>>(() => {
        if (!isLoaded) {
          return LoadingValue
        }

        if (columnData) {
          return withValue(columnData)
        }

        return EmptyValue
      }, [columnData, isLoaded])

      return (
        <RepositoryRenderer
          currentValue={currentValue}
          dropdownRef={dropdownRef}
          model={row.original}
          isDisabled={isDisabled}
        />
      )
    },
    CellEditor({row}) {
      const item = row.original
      const columnId = SystemColumnId.Repository
      if (item.contentType === ItemType.DraftIssue) {
        return <RepositoryEditor model={item} rowIndex={row.index} columnId={columnId} />
      }
      return null
    },
  },
  linkedPullRequests: {
    Placeholder: <LoadingLinkedPullRequestsCell />,
    Cell({row}) {
      const columnId = SystemColumnId.LinkedPullRequests

      const isLoaded = itemHasColumnLoaded(row.original, columnId)

      const columnData = row.original.columns[columnId]

      const currentValue = useMemo<ColumnValue<Array<LinkedPullRequest>>>(() => {
        if (!isLoaded) {
          return LoadingValue
        }

        if (columnData) {
          return withValue(columnData)
        }

        return EmptyValue
      }, [columnData, isLoaded])

      return <LinkedPullRequestsRenderer model={row.original} currentValue={currentValue} />
    },
  },
  reviewers: {
    Placeholder: <LoadingReviewersCell />,
    Cell({row}) {
      const columnId = SystemColumnId.Reviewers

      const isLoaded = itemHasColumnLoaded(row.original, columnId)

      const columnData = row.original.columns[columnId]

      const currentValue = useMemo<ColumnValue<Array<UserGroupItem>>>(() => {
        if (!isLoaded) {
          return LoadingValue
        }

        if (columnData) {
          const users = columnData.map(itemFromReview)
          return withValue(users)
        }

        return EmptyValue
      }, [columnData, isLoaded])

      return <ReviewersRenderer model={row.original} currentValue={currentValue} />
    },
  },
  issueType: {
    Placeholder: <LoadingIssueTypeCell />,
    Cell({row, dropdownRef, isDisabled}) {
      const columnId = SystemColumnId.IssueType

      const isLoaded = itemHasColumnLoaded(row.original, columnId)

      const columnData = row.original.columns[columnId]

      const currentValue = useMemo<ColumnValue<IssueType>>(() => {
        if (!isLoaded) {
          return LoadingValue
        }

        if (columnData) {
          return withValue(columnData)
        }

        return EmptyValue
      }, [columnData, isLoaded])

      return (
        <IssueTypeRenderer
          model={row.original}
          currentValue={currentValue}
          dropdownRef={dropdownRef}
          isDisabled={isDisabled}
        />
      )
    },
    CellEditor({row}) {
      const item = row.original

      const columnId = SystemColumnId.IssueType

      const isLoaded = itemHasColumnLoaded(row.original, columnId)

      const columnData = row.original.columns[columnId]

      const currentValue = useMemo<ColumnValue<IssueType>>(() => {
        if (!isLoaded) {
          return LoadingValue
        }

        if (columnData) {
          return withValue(columnData)
        }

        return EmptyValue
      }, [columnData, isLoaded])

      if (item.contentType === ItemType.DraftIssue || item.contentType === ItemType.Issue) {
        return <IssueTypeEditor currentValue={currentValue} model={item} rowIndex={row.index} columnId={columnId} />
      }
      return null
    },
  },
  parentIssue: {
    Placeholder: <LoadingParentIssueCell />,
    Cell({row, dropdownRef, isDisabled}) {
      const columnId = SystemColumnId.ParentIssue

      const isLoaded = itemHasColumnLoaded(row.original, columnId)

      const columnData = row.original.columns[columnId]

      const currentValue = useMemo<ColumnValue<ParentIssue>>(() => {
        if (!isLoaded) {
          return LoadingValue
        }

        if (columnData) {
          return withValue(columnData)
        }

        return EmptyValue
      }, [columnData, isLoaded])

      return (
        <ParentIssueRenderer
          model={row.original}
          currentValue={currentValue}
          dropdownRef={dropdownRef}
          isDisabled={isDisabled}
        />
      )
    },
    CellEditor({row}) {
      const item = row.original

      const columnId = SystemColumnId.ParentIssue

      const isLoaded = itemHasColumnLoaded(row.original, columnId)

      const columnData = row.original.columns[columnId]

      const currentValue = useMemo<ColumnValue<ParentIssue>>(() => {
        if (!isLoaded) {
          return LoadingValue
        }

        if (columnData) {
          return withValue(columnData)
        }

        return EmptyValue
      }, [columnData, isLoaded])

      if (item.contentType !== ItemType.Issue) return null

      return <ParentIssueEditor currentValue={currentValue} model={item} rowIndex={row.index} columnId={columnId} />
    },
  },
  subIssuesProgress: {
    Placeholder: <LoadingSubIssuesProgressCell />,
    Cell({row}) {
      const columnId = SystemColumnId.SubIssuesProgress

      const isLoaded = itemHasColumnLoaded(row.original, columnId)

      const columnData = row.original.columns[columnId]

      const currentValue = useMemo<ColumnValue<SubIssuesProgress>>(() => {
        if (!isLoaded) {
          return LoadingValue
        }

        if (columnData) {
          return withValue(columnData)
        }

        return EmptyValue
      }, [columnData, isLoaded])

      return <SubIssuesProgressRenderer model={row.original} currentValue={currentValue} />
    },
  },
  created: timestampColumnMeta(SystemColumnId.Created),
  updated: timestampColumnMeta(SystemColumnId.Updated),
  closed: timestampColumnMeta(SystemColumnId.Closed),
}

function buildFieldMeta(column: ColumnModel): Omit<PartialCustomColumnMeta, 'nullWhenRedacted'> {
  const {nullWhenRedacted, ...columnMeta} = columnMetaByFieldDataType[column.dataType]
  if (nullWhenRedacted === false) {
    return columnMeta
  }
  return {
    ...columnMeta,
    Cell: ({row, ...restProps}) => {
      if (row.original.contentType === ItemType.RedactedItem) {
        return null
      }
      return columnMeta.Cell({row, ...restProps})
    },
  }
}

export function buildFieldColumnDef(column: ColumnModel, width: number | undefined): ColumnDef<TableDataType> {
  const columnMeta = buildFieldMeta(column)
  return {
    header: column.name,
    id: column.id.toString(),
    accessorKey: column.id.toString(),
    size: width,
    meta: {
      columnModel: column,
      nonNavigable: false,
      typeToEditEnabled: true,
      ...columnMeta,
    },
  }
}
